
import * as cookie from './cookie.js'
import utils from './index.js'
// import { deviceInfo, isObject, uuid, addUrlParams } from './index.js'

const hostname = location.hostname
const pathname = location.pathname

/** 是否为 InfoQ 或者 写作社区 */
export const isInfoQ = /infoq\.cn$/.test(hostname)

/** 是否为 Geekbang 名下网站 */
export const isGeekbang = /geekbang\.(org|com)$/.test(hostname)

/** 是否为极客时间 */
export const isTime = /^time\.geekbang/.test(hostname)

/** 是否为账户中心 */
export const isAccount = /^account\.geekbang/.test(hostname)

/** 是否为训练营 */
export const isUniversity = /^u\.geekbang/.test(hostname)

/** 是否为部落 */
export const isHorde = /^horde\.geekbang/.test(hostname)

/** 是否为极客搜索 */
export const isSearch = /^s\.geekbang/.test(hostname)

/** 是否为企业版 */
export const isService = /^(b|service)\.geekbang/.test(hostname)

/** 是否为企业版独立学员端 */
export const isEnterprise = isService && /^\/member\/?/.test(pathname)

const config = Symbol('config')
const device = Symbol('device')
const uid = Symbol('uid')
const deviceCookie = 'LF_ID'

class LogFlow {
  constructor (option = {}) {
    this[device] = option.deviceId || cookie.get(deviceCookie)

    this.initConfig(option.config || {})

    this.bindCommonError()
  }

  /**
   * 版本号
   */
  get version () {
    return '1'
  }

  /**
   * 模拟的请求 img
   */
  get req () {
    return new Image()
  }

  /**
   * 登录用户 ID
   */
  get uid () {
    return this[uid] || ''
  }

  /**
   * 访问来源
   * @returns 来源 [infoq: InfoQ, account:账户中心, enterprise:企业学员, service:企业版, time:极客时间]
   */
  get origin () {
    if (isInfoQ) {
      return 'infoq'
    }

    if (isAccount) {
      return 'account'
    }

    if (isEnterprise) {
      return 'enterprise'
    }

    if (isService) {
      return 'service'
    }

    if (isHorde) {
      return 'horde'
    }

    if (isSearch) {
      return 'search'
    }

    return 'time'
  }

  /**
   * 设备信息
   */
  get platform () {
    if (utils.deviceInfo.phone) {
      return 'h5'
    }

    if (utils.deviceInfo.tablet) {
      return 'pad'
    }

    return 'web'
  }

  /**
   * 获取设备 ID，如果初始化时没有传入，或者没有已登录的 ERID GRID，则生成一个随机 ID，并存储到 cookie 中充当唯一标识。
   */
  get deviceID () {
    if (this[device]) {
      return this[device]
    }

    this[device] = cookie.get('ERID') || cookie.get('GRID') || utils.uuid()

    cookie.set(deviceCookie, this[device], {expires: 365})

    return this[device]
  }

  /**
   * 日志库的名称
   */
  get logstore () {
    if (process.env.NODE_ENV === 'development') {
      return 'gktime_web_test'
    }

    switch (this.origin) {
      case 'infoq':
        return 'infoq_web'
      case 'account':
        return 'account_geekbang_web'
      case 'service':
      case 'enterprise':
        return 'gktime_web_es'
      case 'search':
        return 's_geekbang'
      case 'horde':
        return 'horde-fe'
      default:
        return 'gktime_web'
    }
  }

  /**
   * 日志地址的配置信息
   */
  get config () {
    return this[config] || {
      host: 'cn-beijing.log.aliyuncs.com',
      project: 'geek-fe',
      logstore: this.logstore
    }
  }

  /**
   * 公共日志参数
   * @property  {String}     type              事件类型
   * @property  {String}     device_id         设备 ID
   * @property  {String}     type              事件类型
   * @property  {String}     host              当前访问的页面 host
   * @property  {String}     url               当前访问的页面地址
   * @property  {String}     referrer          codument.referrer
   * @property  {String}     app               应用类型 [1:time,2:enterprise,3:infoq,4:account,5:service,6:university]
   * @property  {String}     platform          设备平台 [h5,web]
   * @property  {String}     ua                浏览器的 UA
   * @property  {String}     screen_resolution 屏幕宽高
   * @property  {Timestamp}  timestamp         请求的创建时间
   * @property  {String}     ver               logger 版本号
   */
  get params () {
    return {
      type: 'action_log',
      uid: this.uid,
      device_id: this.deviceID,
      host: location.hostname,
      url: window.location.href,
      referrer: document.referrer,
      level: 'i',
      app: this.origin,
      platform: this.platform,
      ua: navigator.userAgent,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      timestamp: Date.now(),
      ver: this.version
    }
  }

  /**
   * 日志接口地址
   */
  get baseUrl () {
    return `https://${this.config.project}.${this.config.host}/logstores/${this.config.logstore}/track?APIVersion=0.6.0`
  }

  /**
   * 初始化基础配置
   * @param {Object} option            配置信息
   * @param {String} option.host       阿里云日志地址
   * @param {String} option.project    日志项目名称
   * @param {String} option.logstore   日志库名称
   */
  initConfig (option) {
    this[config] = Object.assign({
      host: 'cn-beijing.log.aliyuncs.com',
      project: 'geek-fe',
      logstore: this.logstore
    }, option)
  }

  /**
   * 设置公共的 uid
   * @param {String} uid  用户 ID
   */
  setUid (id) {
    this[uid] = id
  }

  /**
   * 设置公共的异常处理
   */
  bindCommonError () {
    window.onerror = (msg, url, line, col, error) => {
      try {
        this.logger({
          msg: JSON.stringify(arguments),
          level: 'e'
        })
      } catch (error) {
        console.log(error)
      }
    }
  }

  /**
   * 通用基础上报
   * @param {Object}  data       上报的数据
   * @param {String} [data.uid]  用户 ID
   */
  logger (data) {
    const postData = Object.assign(this.params, data)
    const url = utils.addUrlParams(this.baseUrl, postData)

    this.req.src = url
  }

  /**
   * 快捷信息上报
   * @param {String} category  上报类型 [i:info,e:error,w:warning]
   * @param {String} action    上报的事件名称
   * @param {Obejct} params    上报的数据
   */
  quickLogger (category, action, params) {
    let msg = ''
    let level = category
    let uid = this.uid

    // 截取 uid 到顶级类目
    if (utils.isObject(params) && params.uid) {
      uid = params.uid
    }

    try {
      msg = JSON.stringify(params)
    } catch (error) {
      level = 'e'
      msg = `<${action}> params JSON.stringify error`
    }

    this.logger({
      type: action,
      uid,
      level,
      msg
    })
  }

  /**
   * 信息上报
   * @param {String}  action  上报的事件名称
   * @param {Obejct}  [params]  上报的数据
   */
  info (action, params) {
    if (!params) {
      params = action
      action = 'info'
    }

    this.quickLogger('i', action, params)
  }

  /**
   * 警告上报
   * @param {String}  action  上报的事件名称
   * @param {Obejct}  params  上报的数据
   */
  warning (action, params) {
    if (!params) {
      params = action
      action = 'warning'
    }

    this.quickLogger('w', action, params)
  }

  /**
   * 错误上报
   * @param {String}  action  上报的事件名称
   * @param {Obejct}  params  上报的数据
   */
  error (action, params) {
    if (!params) {
      params = action
      action = 'error'
    }

    this.quickLogger('e', action, params)
  }

  /**
   * 请求 trace 上报
   * @param {Obejct}  data            上报的数据
   * @param {String}  data.reqID      请求的唯一标识，uuid@action@moudule
   * @param {String}  data.uid        登录用户 ID
   * @param {String}  data.url        请求的地址
   * @param {String}  data.method     请求方式
   * @param {Number}  data.http_code  response 返回 code
   * @param {Number}  data.cost       请求耗时, 单位:ms
   * @param {JSON}    data.param      上报的数据
   * @param {JSON}    data.msg        错误信息
   */
  request (data) {
    const postData = {
      req_id: data.reqID,
      type: 'request',
      uid: data.uid || this.uid,
      url: data.url,
      method: data.method,
      referrer: window.location.href,
      http_code: data.http_code,
      cost: (data.cost / 1000).toFixed(3)
    }
    let level = !(data.http_code - 200) ? 'i' : 'e'
    let param = ''
    let msg = ''
    let extra = ''

    try {
      param = JSON.stringify(data.param) || ''
      msg = JSON.stringify(data.msg)
      extra = JSON.stringify(data.extra)
    } catch (error) {
      level = 'e'
      param = `<${data.req_id}> params JSON.stringify error`
      msg = `<${data.req_id}> msg JSON.stringify error`
      extra = `<${data.req_id}> extra JSON.stringify error`
    }

    postData.level = level
    postData.param = param.substring(0, 20000)
    postData.msg = msg
    postData.extra = extra

    this.logger(postData)
  }
}

const logger = new LogFlow()

export const plugin = {
  install: function (Vue) {
    Vue.prototype.$logflow = logger
  }
}

export default logger
